import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { Heading } from '@simplywallst/ui-core/next';

import { Truncator } from '@/components/Truncator';

import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';
import ChevronUpSmallSolid from '@simplywallst/ui-core/icons/ChevronUpSmallSolid';

const QA: [string, ReactNode][] = [
  [
    `Is Simply Wall St's portfolio tracker free to use?`,
    `Yes! You can create and manage your investment portfolio for free on Simply Wall St—up to 10 holdings. Free users can add stocks, view insights, and receive updates. Paid plans allow you to track more holdings, create multiple portfolios, and unlock advanced features.`,
  ],
  [
    `How can I create a portfolio on Simply Wall St?`,
    <>
      <p className="m-0 mb-x2">There are three easy ways to get started:</p>
      <ul className="mb-1_75 list-disc space-y-x1 pl-x3">
        <li>
          Link your broker: Securely connect your brokerage account, including
          Robinhood, Fidelity, Charles Schwab, DEGIRO, and over 1,000 other
          supported brokers, to automatically import your holdings. Your
          portfolio refreshes daily with the latest data.
        </li>
        <li>
          Add your holdings manually: Quickly enter stocks, number of shares,
          and average cost for basic tracking of unrealised gains.
        </li>
        <li>
          Import your full transaction history: Upload with our AI import or
          paste your buy and sell transactions for detailed performance
          tracking, including realised and unrealised gains, dividends, currency
          impact, and annualised return (IRR).
        </li>
      </ul>
    </>,
  ],
  [
    `What are the benefits of using Simply Wall St's portfolio tracker?`,
    <>
      <p className="m-0 mb-x2">
        Simply Wall St goes far beyond price tracking. You&apos;ll be able to:
      </p>
      <ul className="mb-x2 list-disc space-y-x1 pl-x3">
        <li>
          Receive relevant updates on your companies, including earnings,
          analyst forecasts, insider activity, and risks.
        </li>

        <li>
          Assign a narrative from our analysts or the community to understand
          the story behind each stock.
        </li>

        <li>Set and track your own valuation for each holding.</li>

        <li>
          Measure diversification by sector and geographic revenue exposure.
        </li>

        <li>
          Benchmark your portfolio against the market using key financial
          metrics.
        </li>

        <li>
          Track detailed returns over any time frame. With transactions added,
          you get a breakdown of realised gains, unrealised gains, dividends,
          currency effects, and your annualised return (IRR).
        </li>
      </ul>
    </>,
  ],
  [
    `Can I track dividends in my portfolio?`,
    `Yes. We track both historical dividends you've received and forecasted income from your current holdings. We also assess dividend quality, so you can avoid unreliable payers and protect your future income stream.`,
  ],
  [
    `Why is Simply Wall St better than other portfolio tools?`,
    <>
      <p className="m-0 mb-x2">
        Most brokers and portfolio trackers only show prices and unrealised
        gains, with little context and lots of noisy news. Simply Wall St
        offers:
      </p>
      <ul className="mb-x2 list-disc space-y-x1 pl-x3">
        <li>
          Detailed return tracking, including all components of your real
          performance.
        </li>
        <li>
          Access to investment narratives to help you understand the companies
          you hold.
        </li>
        <li>
          A full research platform to analyse fundamentals like valuation,
          growth, and financial health.
        </li>
        <li>
          An advanced stock screener to help you discover new investment
          opportunities.
        </li>
      </ul>
      <p className="m-0 mb-x2">
        In short, Simply Wall St is more than a tracker — it&apos;s a complete
        investing platform for monitoring, analysing, and growing your
        portfolio.
      </p>
    </>,
  ],
];

export const DemoPortfolioFAQ = () => {
  const [active, setActive] = useState<string[]>([]);

  const handleClick = useCallback(
    (question) => {
      if (active.includes(question)) {
        setActive((active) => active.filter((n) => n !== question));
      } else {
        setActive((active) => [...active, question]);
      }
    },
    [active]
  );
  return (
    <section className="text-solid">
      <Heading as="h2" className="mb-x2">
        Frequently Asked Questions
      </Heading>
      {QA.map(([question, answer]) => (
        <article
          key={question}
          className="border-0 border-t border-solid border-soft py-x1"
        >
          <div
            onClick={() => handleClick(question)}
            className="grid min-h-x5 cursor-pointer grid-cols-[auto_max-content] items-center"
          >
            <Heading as="h3" className="py-x1_25 text-sm font-strong">
              {question}
            </Heading>
            {active.includes(question) ? (
              <ChevronUpSmallSolid className="fill-softer" />
            ) : (
              <ChevronDownSmallSolid className="fill-soft" />
            )}
          </div>
          <Truncator open={active.includes(question)} minHeight={0}>
            <p className="m-0 pb-x1 text-sm text-soft">{answer}</p>
          </Truncator>
        </article>
      ))}
    </section>
  );
};
